import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';

interface BackendLayoutProps {
  children: React.ReactNode;
}

function NavLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
        isActive
          ? 'bg-white/10 text-white'
          : 'text-white/60 hover:text-white hover:bg-white/5'
      }`}
    >
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-lg`}></i>
      </div>
      <span>{label}</span>
    </Link>
  );
}

export default function BackendLayout({ children }: BackendLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#0A0A0A] border-r border-white/5 z-50 flex flex-col transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[260px]'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-white/5 ${collapsed ? 'justify-center px-0' : 'px-6'}`}>
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-8 h-8 bg-[#00FF88] flex items-center justify-center flex-shrink-0">
              <i className="ri-links-line text-black text-lg"></i>
            </div>
            {!collapsed && (
              <span className="text-white font-bold text-lg tracking-tight">AFFILIX</span>
            )}
          </Link>
        </div>

        {/* Nav links */}
        <nav className={`flex-1 py-6 space-y-1 ${collapsed ? 'px-2' : 'px-4'}`}>
          {!collapsed ? (
            <>
              <NavLink to="/dashboard" icon="ri-dashboard-line" label="Dashboard" />
              <NavLink to="/discover" icon="ri-compass-discover-line" label="Discover AGI" />
              <NavLink to="/affiliate-links" icon="ri-links-line" label="Affiliate Links" />
              <NavLink to="/products-management" icon="ri-shopping-bag-line" label="Products" />
              <NavLink to="/outreach" icon="ri-mail-send-line" label="Outreach" />
              <NavLink to="/partners" icon="ri-team-line" label="Partners" />
            </>
          ) : (
            <>
              <Link to="/dashboard" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-dashboard-line text-lg"></i>
                </div>
              </Link>
              <Link to="/discover" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-compass-discover-line text-lg"></i>
                </div>
              </Link>
              <Link to="/affiliate-links" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-links-line text-lg"></i>
                </div>
              </Link>
              <Link to="/products-management" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-lg"></i>
                </div>
              </Link>
              <Link to="/outreach" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-send-line text-lg"></i>
                </div>
              </Link>
              <Link to="/partners" className="flex items-center justify-center py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-team-line text-lg"></i>
                </div>
              </Link>
            </>
          )}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:block p-3 border-t border-white/5">
          <button
            onClick={() => setCollapsed((prev) => !prev)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/40 hover:text-white hover:bg-white/5 transition-all cursor-pointer ${collapsed ? 'justify-center' : ''}`}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-lg`}></i>
            </div>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        {/* Back to site */}
        <div className="p-3 border-t border-white/5">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/40 hover:text-white hover:bg-white/5 transition-all cursor-pointer ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Back to Site' : undefined}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-left-line text-lg"></i>
            </div>
            {!collapsed && <span>Back to Site</span>}
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}>
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>

          <div className="hidden lg:block" />

          <div className="flex items-center gap-4">
            <button className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer relative">
              <i className="ri-notification-3-line text-xl"></i>
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="w-9 h-9 bg-[#0A0A0A] rounded-full flex items-center justify-center cursor-pointer">
              <i className="ri-user-line text-white text-sm"></i>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
